import type {
  Conversation,
  Message,
  PaginatedConversations,
  PaginatedMessages,
  PushConfig,
  PushSubscriptionPayload,
  User,
} from "./types";


const API_BASE = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "");

export class ApiError extends Error {
  status: number;
  code: string;

  constructor(message: string, status: number, code = "api_error") {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  token?: string | null,
): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (token) {
    headers.set("Authorization", `Token ${token}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : null;
  if (!response.ok) {
    const detail =
      payload?.error?.detail ||
      payload?.detail ||
      `Richiesta non riuscita (HTTP ${response.status}).`;
    throw new ApiError(detail, response.status, payload?.error?.code);
  }
  return payload as T;
}

export async function login(username: string, password: string) {
  return request<{ token: string; user: User }>("/auth/login/", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

export function logout(token: string) {
  return request<void>("/auth/logout/", { method: "POST" }, token);
}

export function getMe(token: string) {
  return request<User>("/auth/me/", {}, token);
}

export function getConversations(
  token: string,
  search = "",
  mode = "",
  signal?: AbortSignal,
) {
  const params = new URLSearchParams({ page_size: "100" });
  if (search) params.set("search", search);
  if (mode) params.set("mode", mode);
  return request<PaginatedConversations>(
    `/conversations/?${params}`,
    { signal },
    token,
  );
}

export function getConversation(token: string, conversationId: number) {
  return request<Conversation>(`/conversations/${conversationId}/`, {}, token);
}

export function getMessages(
  token: string,
  conversationId: number,
  beforeId?: number | null,
  signal?: AbortSignal,
) {
  const params = new URLSearchParams({ limit: "60" });
  if (beforeId) params.set("before_id", String(beforeId));
  return request<PaginatedMessages>(
    `/conversations/${conversationId}/messages/?${params}`,
    { signal },
    token,
  );
}

export function getPushConfig(token: string) {
  return request<PushConfig>("/push/config/", {}, token);
}

export function savePushSubscription(
  token: string,
  subscription: PushSubscriptionPayload,
) {
  return request<{ id: number; active: boolean }>(
    "/push/subscriptions/",
    {
      method: "POST",
      body: JSON.stringify(subscription),
    },
    token,
  );
}

export function deletePushSubscription(token: string, endpoint: string) {
  return request<void>(
    "/push/subscriptions/",
    {
      method: "DELETE",
      body: JSON.stringify({ endpoint }),
    },
    token,
  );
}

export function setConversationMode(
  token: string,
  conversationId: number,
  mode: "ai" | "human",
) {
  return request<Conversation>(
    `/conversations/${conversationId}/mode/`,
    {
      method: "POST",
      body: JSON.stringify({ mode }),
    },
    token,
  );
}

export function sendMessage(token: string, conversationId: number, text: string) {
  return request<Message>(
    `/conversations/${conversationId}/send/`,
    {
      method: "POST",
      body: JSON.stringify({
        text,
        client_request_id: crypto.randomUUID(),
      }),
    },
    token,
  );
}

export async function downloadAttachment(
  token: string,
  downloadUrl: string,
  filename: string,
) {
  const headers = new Headers({ Authorization: `Token ${token}` });
  const response = await fetch(downloadUrl, { headers });
  if (!response.ok) {
    let detail = "Impossibile scaricare l'allegato.";
    try {
      const payload = await response.json();
      detail = payload?.error?.detail || detail;
    } catch {
      // La risposta può non essere JSON.
    }
    throw new ApiError(detail, response.status, "attachment_download_failed");
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename || "allegato";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}
